// =============================================================================
// lib/orgDateTime.ts — Fonte única do fuso da organização no `apps/web`
// (F38-S10, docs/planejamento-livechat-contexto-comercial.md §4).
//
// Por que este arquivo existe:
//   Em 2026-08-24 o front tinha 14+ pontos cravando a constante
//   'America/Porto_Velho' — cada slot criou a sua cópia porque as demais
//   estavam fora do `files_allowed` daquele slot (ver histórico em
//   hooks/sales/orgTimezone.ts, features/sales/agenda/timezone.ts,
//   features/sales/campaigns/timezone.ts, features/sales/components/
//   dateTimeOrgTz.ts, features/conversations/components/ChatList/
//   dateTimeOrgTz.ts — todos agora reexportam este módulo). É a MESMA classe
//   do bug `computeRange` (três cópias da função de intervalo, servidor em
//   UTC, operação em UTC-4, filtro "Hoje" começando às 20h do dia anterior).
//
// O que ainda falta (follow-up explícito, NÃO resolvido aqui):
//   O backend já resolve o fuso por organização — `organizations.timezone`,
//   lido por `apps/api/src/shared/org-timezone.ts::getOrganizationTimezone`
//   e usado no filtro `returnToday` (F38-S02) e no efetivo de
//   `GET/PATCH /api/sales/settings` (F34-S02). Mas NENHUM contrato que o
//   front já consome de forma universal expõe esse campo:
//     - `AuthUser` (lib/auth-store.ts, populado por login/refresh) não tem
//       `timezone` — é o único payload que todo papel autenticado recebe.
//     - `GET /api/sales/settings` devolve o fuso EFETIVO, mas está atrás de
//       `sales_campaigns:manage` + flag `sales.workspace.enabled` — não é
//       fonte confiável para dashboard, CRM e conversas, que qualquer agente
//       vê sem essa permissão.
//   Inventar aqui um endpoint novo, ou adivinhar pelo fuso do navegador
//   (`Intl.DateTimeFormat().resolvedOptions().timeZone`), trocaria um bug
//   determinístico (a constante) por um não-determinístico — pior (lição
//   explícita deste slot). Por isso o módulo já nasce parametrizável
//   (`setOrgTimeZone`/`getOrgTimeZone`) mas ninguém ainda chama
//   `setOrgTimeZone` com dado real — é UM ponto de wiring, não 14.
//   Follow-up: expor `timezone` no payload de auth (ou em endpoint próprio
//   sem gate de permissão) é slot de BACKEND — fora do escopo daqui.
//
// Fuso fixo, sem horário de verão (Rondônia não observa DST desde 2019) —
// mesma convenção de apps/api/src/modules/reports/export/pdf.ts. Quando uma
// organização fora de Rondônia (white-label, F17/F18) tiver fuso diferente,
// basta chamar `setOrgTimeZone` com o valor real — todas as funções abaixo
// já aceitam um `timeZone` explícito como último parâmetro (usado pelos
// testes para provar que a formatação muda com o fuso).
// =============================================================================

/**
 * Fallback explícito — usado SOMENTE enquanto nenhum dado real de
 * organização foi carregado (ver cabeçalho). NUNCA tratar como "o fuso
 * certo": é o fuso da única organização em operação hoje (Rondônia, UTC-4),
 * não uma verdade universal.
 *
 * ⚠️ NÃO use esta constante (nem o alias legado `ORG_TIMEZONE`, mantido nos
 * reexports) para descobrir "o fuso atual" — ela é ESTÁTICA e continuará
 * apontando para Rondônia mesmo depois que `setOrgTimeZone` receber o dado
 * real (F38-S11). Ler o fallback achando que é o fuso ativo recria exatamente
 * a divergência backend×frontend que o F38-S10 eliminou.
 *
 * Para o fuso ativo: `getOrgTimeZone()`, ou o parâmetro `timeZone` das funções
 * deste módulo (que já usam `getOrgTimeZone()` como default).
 */
export const FALLBACK_ORG_TIMEZONE = 'America/Porto_Velho';

let currentOrgTimeZone = FALLBACK_ORG_TIMEZONE;

/**
 * Atualiza o fuso da organização usado por padrão em todas as funções deste
 * módulo. Ponto único de wiring quando o contrato de auth passar a expor
 * `organizations.timezone` (follow-up, ver cabeçalho) — hoje nada chama
 * esta função em código de produção.
 */
export function setOrgTimeZone(timeZone: string): void {
  const trimmed = timeZone.trim();
  currentOrgTimeZone = trimmed.length > 0 ? trimmed : FALLBACK_ORG_TIMEZONE;
}

/** Fuso atualmente ativo — o dado real quando `setOrgTimeZone` já rodou, o fallback caso contrário. */
export function getOrgTimeZone(): string {
  return currentOrgTimeZone;
}

/** Reseta para o fallback — uso exclusivo de testes (`afterEach`). */
export function resetOrgTimeZoneForTests(): void {
  currentOrgTimeZone = FALLBACK_ORG_TIMEZONE;
}

// ---------------------------------------------------------------------------
// Helpers internos
// ---------------------------------------------------------------------------

function toDate(input: Date | string): Date {
  return typeof input === 'string' ? new Date(input) : input;
}

function dayPartsInTz(date: Date, timeZone: string): { year: number; month: number; day: number } {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = formatter.formatToParts(date);
  const get = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return { year: get('year'), month: get('month'), day: get('day') };
}

function tzOffsetMinutes(date: Date, timeZone: string): number {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  const parts = formatter.formatToParts(date);
  const get = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? 0);
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  );
  return (asUtc - date.getTime()) / 60_000;
}

// ---------------------------------------------------------------------------
// Formatação — todas guardam contra data inválida (nunca lançam, nunca
// mostram "Invalid Date"; devolvem '' e deixam o chamador decidir o vazio).
// ---------------------------------------------------------------------------

/** "25/08/2026 14:00" — data + hora, no fuso da organização (padrão, com ano). */
export function formatOrgDateTime(
  input: Date | string,
  timeZone: string = getOrgTimeZone(),
): string {
  const date = toDate(input);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/** "25/08 14:00" — data + hora compacta (sem ano), para badges/rodapés estreitos. */
export function formatOrgDateTimeShort(
  input: Date | string,
  timeZone: string = getOrgTimeZone(),
): string {
  const date = toDate(input);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone,
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/** "25/08/2026" — só a data, no fuso da organização. */
export function formatOrgDate(input: Date | string, timeZone: string = getOrgTimeZone()): string {
  const date = toDate(input);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

/** "14:00" — só a hora, no fuso da organização. */
export function formatOrgTime(input: Date | string, timeZone: string = getOrgTimeZone()): string {
  const date = toDate(input);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/** "25/08" — data curta (dia/mês), no fuso da organização. */
export function formatOrgDayMonth(
  input: Date | string,
  timeZone: string = getOrgTimeZone(),
): string {
  const date = toDate(input);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone,
    day: '2-digit',
    month: '2-digit',
  }).format(date);
}

/** "sexta-feira, 21 de agosto" — usado no agrupamento por dia (modo calendário). */
export function formatOrgWeekdayDate(
  input: Date | string,
  timeZone: string = getOrgTimeZone(),
): string {
  const date = toDate(input);
  if (Number.isNaN(date.getTime())) return '';
  const label = new Intl.DateTimeFormat('pt-BR', {
    timeZone,
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  }).format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** "Sexta-feira, 21 de agosto de 2026" — usado no cabeçalho de página. */
export function formatOrgFullDate(
  input: Date | string,
  timeZone: string = getOrgTimeZone(),
): string {
  const date = toDate(input);
  if (Number.isNaN(date.getTime())) return '';
  const label = new Intl.DateTimeFormat('pt-BR', {
    timeZone,
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

// ---------------------------------------------------------------------------
// Bordas de dia — chave de dia civil, "é hoje?", início/fim do dia local.
// ---------------------------------------------------------------------------

/** "2026-08-25" — chave do dia civil no fuso da organização (agrupamento/comparação). */
export function orgDayKey(input: Date | string, timeZone: string = getOrgTimeZone()): string {
  const date = toDate(input);
  const { year, month, day } = dayPartsInTz(date, timeZone);
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * `true` quando `input` cai no mesmo dia-calendário de `reference`, ambos
 * avaliados no fuso da organização (NUNCA o fuso do navegador).
 * `reference` é injetável para testes determinísticos. Data inválida → `false`
 * (nunca marca como "hoje" por acidente).
 */
export function isTodayInOrgTz(
  input: Date | string,
  reference: Date = new Date(),
  timeZone: string = getOrgTimeZone(),
): boolean {
  const date = toDate(input);
  if (Number.isNaN(date.getTime())) return false;
  return orgDayKey(date, timeZone) === orgDayKey(reference, timeZone);
}

/** Instante UTC correspondente à meia-noite local (fuso da organização) do dia de `reference`. */
export function startOfDayInOrgTz(reference: Date, timeZone: string = getOrgTimeZone()): Date {
  const { year, month, day } = dayPartsInTz(reference, timeZone);
  const guessMs = Date.UTC(year, month - 1, day, 0, 0, 0);
  const guess = new Date(guessMs);
  const offsetMin = tzOffsetMinutes(guess, timeZone);
  return new Date(guessMs - offsetMin * 60_000);
}

/** Último instante (23:59:59.999) do dia local de `reference`, no fuso da organização. */
export function endOfDayInOrgTz(reference: Date, timeZone: string = getOrgTimeZone()): Date {
  const start = startOfDayInOrgTz(reference, timeZone);
  return new Date(start.getTime() + 24 * 60 * 60 * 1000 - 1);
}

// ---------------------------------------------------------------------------
// Conversão local↔UTC — formulários de agendamento (data/hora digitadas no
// fuso da organização → instante UTC para a API, e o inverso).
// ---------------------------------------------------------------------------

/**
 * Converte um par (data "YYYY-MM-DD", hora "HH:mm") interpretado no fuso da
 * organização para um instante UTC (ISO 8601), pronto para `scheduledAt` da
 * API. Retorna `null` para entrada vazia/incompleta/inválida.
 */
export function orgLocalToUtcIso(
  dateStr: string,
  timeStr: string,
  timeZone: string = getOrgTimeZone(),
): string | null {
  if (!dateStr || !timeStr) return null;
  const [yearStr, monthStr, dayStr] = dateStr.split('-');
  const [hourStr, minuteStr] = timeStr.split(':');
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  const hour = Number(hourStr);
  const minute = Number(minuteStr);
  if ([year, month, day, hour, minute].some((n) => Number.isNaN(n))) return null;

  const guessMs = Date.UTC(year, month - 1, day, hour, minute, 0);
  const guess = new Date(guessMs);
  const offsetMin = tzOffsetMinutes(guess, timeZone);
  return new Date(guessMs - offsetMin * 60_000).toISOString();
}

/** Instante UTC (ISO) → partes locais no fuso da organização, para pré-popular formulários. */
export function utcIsoToOrgLocalParts(
  iso: string,
  timeZone: string = getOrgTimeZone(),
): { date: string; time: string } {
  const date = new Date(iso);
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const parts = formatter.formatToParts(date);
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? '00';
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    time: `${get('hour')}:${get('minute')}`,
  };
}

/** "HH:mm" default para um novo agendamento — próxima hora cheia, no fuso da organização. */
export function defaultTimeOrgTz(
  reference: Date,
  timeZone: string = getOrgTimeZone(),
): { date: string; time: string } {
  const nextHour = new Date(reference.getTime());
  nextHour.setUTCMinutes(0, 0, 0);
  nextHour.setUTCHours(nextHour.getUTCHours() + 1);
  return utcIsoToOrgLocalParts(nextHour.toISOString(), timeZone);
}

// ---------------------------------------------------------------------------
// Aritmética pura — sem dependência de fuso (ms corridos).
// ---------------------------------------------------------------------------

/** Soma `days` dias corridos (24h) a um instante. */
export function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * 24 * 60 * 60 * 1000);
}

/** Soma `minutes` minutos a um instante. */
export function addMinutes(date: Date, minutes: number): Date {
  return new Date(date.getTime() + minutes * 60_000);
}
